// ============================================================
// ByOurConsent.com -- Law vs Action Checker
// /premium/law-checker
// Premium member tool -- Civic Intelligence Suite
// ============================================================

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Nav    from '../components/Nav';
import Footer from '../components/Footer';
import { downloadReportAsWord } from '../utils/downloadRtf';

// ============================================================
// Data
// ============================================================
const US_STATES = [
    'Alabama','Alaska','Arizona','Arkansas','California','Colorado','Connecticut',
    'Delaware','Florida','Georgia','Hawaii','Idaho','Illinois','Indiana','Iowa',
    'Kansas','Kentucky','Louisiana','Maine','Maryland','Massachusetts','Michigan',
    'Minnesota','Mississippi','Missouri','Montana','Nebraska','Nevada','New Hampshire',
    'New Jersey','New Mexico','New York','North Carolina','North Dakota','Ohio',
    'Oklahoma','Oregon','Pennsylvania','Rhode Island','South Carolina','South Dakota',
    'Tennessee','Texas','Utah','Vermont','Virginia','Washington','West Virginia',
    'Wisconsin','Wyoming'
];

const DOCUMENT_TYPES = [
    { id: 'federal_law',    label: 'Federal law or statute',           desc: 'An act of Congress signed into law' },
    { id: 'executive_order',label: 'Executive order or proclamation',  desc: 'Presidential or gubernatorial directive' },
    { id: 'regulation',     label: 'Federal regulation or rule',       desc: 'Agency rule published in the Federal Register' },
    { id: 'state_law',      label: 'State law or statute',             desc: 'A law passed by a state legislature' },
    { id: 'local_ordinance',label: 'Local ordinance or resolution',    desc: 'City council, county, or municipal law' },
    { id: 'agency_policy',  label: 'Agency policy or directive',       desc: 'Policy issued by a government agency or department' },
    { id: 'court_order',    label: 'Court order or injunction',        desc: 'Order issued by a judge or court' },
    { id: 'other',          label: 'Other government action or document', desc: 'Any other official government document or action' },
];

const ANALYSIS_TYPES = [
    { id: 'plain_english', label: 'Plain English translation',      desc: 'What does this actually say and do?' },
    { id: 'authority',     label: 'Authority analysis',             desc: 'Does the issuing body have the legal power to do this?' },
    { id: 'constitutional',label: 'Constitutional analysis',        desc: 'Does it violate the US or state constitution?' },
    { id: 'precedent',     label: 'Legal precedent',                desc: 'Have similar laws been challenged or struck down?' },
    { id: 'impact',        label: 'Practical impact on citizens',   desc: 'What does this mean for ordinary people?' },
    { id: 'challenge',     label: 'How to challenge it',            desc: 'Legal avenues available to oppose or challenge this.' },
];

// ============================================================
// Step indicator
// ============================================================
const StepIndicator = ({ step, total, onStepClick }) => (
    <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '2rem' }}>
        {Array.from({ length: total }, (_, i) => (
            <React.Fragment key={i}>
                <div
                    onClick={() => i + 1 < step && onStepClick && onStepClick(i + 1)}
                    title={i + 1 < step ? 'Click to go back' : ''}
                    style={{
                        width: '28px', height: '28px', borderRadius: '50%',
                        background: i + 1 <= step ? '#8b1a1a' : '#ede8dc',
                        border: `0.5px solid ${i + 1 <= step ? '#8b1a1a' : '#c8beaa'}`,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: '12px', fontFamily: 'sans-serif', fontWeight: 600,
                        color: i + 1 <= step ? '#f5f0e8' : '#8a8070',
                        cursor: i + 1 < step ? 'pointer' : 'default'
                    }}>
                    {i + 1 < step ? '✓' : i + 1}
                </div>
                {i < total - 1 && (
                    <div style={{ flex: 1, height: '1px', background: i + 1 < step ? '#8b1a1a' : '#c8beaa' }} />
                )}
            </React.Fragment>
        ))}
    </div>
);

// ============================================================
// Styles
// ============================================================
const iStyle = {
    width: '100%', background: '#f5f0e8', border: '0.5px solid #c8beaa',
    padding: '8px 12px', fontSize: '13px', fontFamily: 'sans-serif',
    color: '#1e2a3a', borderRadius: '3px', outline: 'none', boxSizing: 'border-box'
};

const lStyle = {
    fontSize: '11px', fontFamily: 'sans-serif', color: '#6a7080',
    letterSpacing: '0.08em', textTransform: 'uppercase', display: 'block', marginBottom: '4px'
};

const btnPrimary = {
    background: '#8b1a1a', color: '#f5f0e8', border: 'none',
    padding: '10px 24px', fontSize: '13px', fontFamily: 'sans-serif',
    letterSpacing: '0.06em', textTransform: 'uppercase', borderRadius: '3px', cursor: 'pointer'
};

const btnSecondary = {
    background: 'transparent', color: '#4a5060', border: '0.5px solid #c8beaa',
    padding: '10px 20px', fontSize: '13px', fontFamily: 'sans-serif',
    borderRadius: '3px', cursor: 'pointer'
};

// ============================================================
// Main component
// ============================================================
const LawCheckerPage = () => {
    const [step,    setStep]    = useState(1);
    const [loading, setLoading] = useState(false);
    const [result,  setResult]  = useState(null);
    const [error,   setError]   = useState('');

    const [form, setForm] = useState({
        docType:       '',
        level:         'federal',
        state:         '',
        docTitle:      '',
        docText:       '',
        issuer:        '',
        analysisTypes: ['plain_english', 'authority', 'constitutional', 'precedent', 'impact', 'challenge'],
        concerns:      ''
    });

    const set = (field, value) => setForm(p => ({ ...p, [field]: value }));

    const toggleAnalysis = (id) => {
        setForm(p => ({
            ...p,
            analysisTypes: p.analysisTypes.includes(id)
                ? p.analysisTypes.filter(a => a !== id)
                : [...p.analysisTypes, id]
        }));
    };

    const selectedDocType = DOCUMENT_TYPES.find(d => d.id === form.docType);

    const canProceedStep1 = form.docType &&
        form.docText.trim().length > 50;

    // ============================================================
    // Generate analysis
    // ============================================================
    const generate = async () => {
        setLoading(true);
        setError('');

        const selectedAnalysis = ANALYSIS_TYPES
            .filter(a => form.analysisTypes.includes(a.id))
            .map(a => a.label)
            .join(', ');

        const levelDesc = form.level === 'federal'
            ? 'Federal'
            : `${form.state} State`;

        const prompt = `You are an expert constitutional law scholar, administrative law specialist, and legal analyst. Analyze the following government document or law.

DOCUMENT DETAILS:
- Type: ${selectedDocType?.label}
- Level: ${levelDesc}
${form.issuer ? `- Issuing authority: ${form.issuer}` : ''}
${form.docTitle ? `- Title/identifier: ${form.docTitle}` : ''}

DOCUMENT TEXT:
${form.docText}

${form.concerns ? `SPECIFIC CONCERNS FROM REVIEWER:\n${form.concerns}` : ''}

REQUESTED ANALYSIS: ${selectedAnalysis}

Provide a comprehensive analysis with the following sections:

${form.analysisTypes.includes('plain_english') ? `
## PLAIN ENGLISH TRANSLATION
What does this document actually say and do? Explain it clearly:
- The stated purpose vs what it actually accomplishes
- Who it affects and how
- Key requirements, prohibitions, or permissions it creates
- What changes from existing law or policy
- Any definitions buried in the text that change its meaning
` : ''}

${form.analysisTypes.includes('authority') ? `
## AUTHORITY ANALYSIS
Does the issuing body have legal power to do this?
- What is the claimed legal authority or basis?
- Does that authority actually permit this action?
- Any delegation of authority issues (can an agency do what only Congress can do?)
- Separation of powers concerns
- Non-delegation doctrine issues if applicable
- Rate the authority concern: None / Minor / Moderate / Significant / Severe
` : ''}

${form.analysisTypes.includes('constitutional') ? `
## CONSTITUTIONAL ANALYSIS
Does this comply with the Constitution?
- Which constitutional provisions are implicated (cite specific amendments, articles, clauses)
- First Amendment concerns (speech, religion, assembly, press)
- Fourth Amendment concerns (search and seizure, privacy)
- Fifth/Fourteenth Amendment concerns (due process, equal protection, takings)
- Any other constitutional provisions at issue
- Strength of constitutional challenge: Weak / Moderate / Strong / Very Strong
- Key constitutional tests that would apply (rational basis, strict scrutiny, etc.)
` : ''}

${form.analysisTypes.includes('precedent') ? `
## LEGAL PRECEDENT
Have similar laws or actions been challenged?
- Relevant Supreme Court decisions (cite case names and years)
- Lower court decisions on similar issues
- Laws or orders that were struck down on similar grounds
- Laws that survived similar challenges and why
- How courts have historically treated this type of government action
` : ''}

${form.analysisTypes.includes('impact') ? `
## PRACTICAL IMPACT ON CITIZENS
What does this mean for ordinary people?
- Direct effects on daily life, rights, or finances
- Who benefits and who is burdened
- Enforcement mechanisms and penalties
- Compliance requirements
- Timeline for implementation
- Any exemptions or carve-outs that affect who it applies to
` : ''}

${form.analysisTypes.includes('challenge') ? `
## HOW TO CHALLENGE IT
Legal avenues available:
- Grounds for legal challenge (constitutional, statutory, procedural)
- Who has standing to sue
- Which courts would have jurisdiction
- Administrative remedies that must be exhausted first
- Organizations that litigate these types of cases
- Citizen actions beyond legal challenge (comment periods, legislative pressure, ballot measures)
- Realistic assessment of challenge success
` : ''}

## BOTTOM LINE ASSESSMENT
- Overall constitutional risk rating: Low / Moderate / High / Very High
- Most significant concerns in order of importance
- 3-5 key takeaways every citizen should know about this document

Be precise, cite specific constitutional provisions and case law, and be honest about areas of genuine legal uncertainty vs clear violations. If something is clearly constitutional, say so.`;

        try {
            const response = await fetch('/api/ai/generate', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('boc_token')}`
                },
                body: JSON.stringify({ prompt, max_tokens: 3000 })
            });

            const data = await response.json();
            if (!response.ok) throw new Error(data.error || 'Generation failed');
            const text = data.text || '';
            if (!text) throw new Error('No response generated');

            setResult({
                analysis: text,
                docTitle: form.docTitle || selectedDocType?.label,
                docType: selectedDocType?.label
            });
            setStep(3);
        } catch (err) {
            setError('Failed to generate analysis. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    // ============================================================
    // Download
    // ============================================================
    const downloadWord = () => {
        downloadReportAsWord(
            result.analysis,
            `Law-Analysis-${(result.docTitle || 'document').replace(/[^a-z0-9]/gi,'-').slice(0,40)}`
        );
    };

    const reset = () => {
        setStep(1); setResult(null); setError('');
        setForm({ docType: '', level: 'federal', state: '', docTitle: '', docText: '',
            issuer: '', analysisTypes: ['plain_english','authority','constitutional','precedent','impact','challenge'], concerns: '' });
    };

    // ============================================================
    // Render
    // ============================================================
    return (
        <>
            <Helmet><title>Law vs Action Checker — By Our Consent</title></Helmet>
            <Nav />

            {/* Header */}
            <div style={{ background: '#1e2a3a', borderBottom: '2px solid #8b1a1a', padding: '1.5rem' }}>
                <div style={{ maxWidth: '720px', margin: '0 auto' }}>
                    <Link to="/premium" style={{ fontSize: '11px', fontFamily: 'sans-serif', color: '#8a9aaa', textDecoration: 'none', letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                        ← Premium tools
                    </Link>
                    <div style={{ fontSize: '9px', fontFamily: 'sans-serif', letterSpacing: '0.16em', textTransform: 'uppercase', color: '#8b9a7a', marginTop: '10px' }}>
                        Civic intelligence suite
                    </div>
                    <h1 style={{ fontSize: '28px', fontWeight: 600, color: '#e8dfc8', fontFamily: "'EB Garamond', Georgia, serif", marginTop: '4px' }}>
                        Law vs Action Checker
                    </h1>
                    <p style={{ fontSize: '13px', fontFamily: 'sans-serif', color: '#8a9aaa', marginTop: '6px' }}>
                        Paste any law, regulation, executive order, or government policy and get a plain-language
                        analysis of what it actually does, whether it exceeds legal authority,
                        and whether it is constitutional.
                    </p>
                </div>
            </div>

            <div style={{ maxWidth: '720px', margin: '0 auto', padding: '2rem 1.5rem' }}>

                <StepIndicator step={step} total={3} onStepClick={setStep} />

                {/* ── STEP 1: Document ── */}
                {step === 1 && (
                    <div>
                        <div style={{ fontSize: '18px', fontWeight: 600, color: '#1e2a3a', fontFamily: "'EB Garamond', Georgia, serif", marginBottom: '1.5rem' }}>
                            Step 1 — Provide the document
                        </div>

                        {/* Help panel */}
                        <details style={{ marginBottom: '1.5rem', background: '#ede8dc', border: '0.5px solid #c8beaa', borderRadius: '3px' }}>
                            <summary style={{ padding: '10px 14px', fontSize: '12px', fontFamily: 'sans-serif', color: '#8b6a3a', fontWeight: 600, cursor: 'pointer', letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                                💡 How to get the best results
                            </summary>
                            <div style={{ padding: '0 14px 14px', fontSize: '12px', fontFamily: 'sans-serif', color: '#4a5060', lineHeight: 1.7 }}>

                                <div style={{ fontWeight: 600, color: '#1e2a3a', marginBottom: '4px', marginTop: '10px' }}>What to paste — the actual text, not a summary</div>
                                <div style={{ marginBottom: '6px' }}>
                                    Paste the actual legal text of the law, order, or regulation — not a news article about it.
                                    The more text you provide, the more accurate the analysis. You can paste sections rather
                                    than the entire document if it is very long.
                                </div>
                                <div style={{ background: '#f5f0e8', border: '0.5px solid #c8beaa', borderRadius: '3px', padding: '8px 10px', marginBottom: '10px', fontSize: '11px', color: '#6a7080' }}>
                                    <strong style={{ color: '#1e2a3a' }}>Where to find it:</strong> Federal laws at <a href="https://congress.gov" target="_blank" rel="noopener noreferrer" style={{ color: '#8b1a1a' }}>congress.gov</a> or
                                    <a href="https://federalregister.gov" target="_blank" rel="noopener noreferrer" style={{ color: '#8b1a1a' }}> federalregister.gov</a>.
                                    Executive orders at <a href="https://federalregister.gov/presidential-documents/executive-orders" target="_blank" rel="noopener noreferrer" style={{ color: '#8b1a1a' }}>federalregister.gov</a>.
                                    State laws at your state legislature website.
                                </div>

                                <div style={{ fontWeight: 600, color: '#1e2a3a', marginBottom: '4px' }}>Specific concerns — focus the analysis</div>
                                <div style={{ marginBottom: '6px' }}>
                                    If you have a specific concern about a particular section or provision, note it.
                                    This helps the AI focus on what matters most to you.
                                </div>
                                <div style={{ background: '#f5f0e8', border: '0.5px solid #c8beaa', borderRadius: '3px', padding: '8px 10px', fontSize: '11px', color: '#6a7080' }}>
                                    <strong style={{ color: '#1e2a3a' }}>Example:</strong> "I am particularly concerned about Section 3(b) which requires
                                    private businesses to report customer data to the government without a warrant.
                                    Is this a Fourth Amendment violation?"
                                </div>
                            </div>
                        </details>

                        {/* Document type */}
                        <div style={{ marginBottom: '1.25rem' }}>
                            <label style={lStyle}>Document type</label>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                {DOCUMENT_TYPES.map(d => (
                                    <label key={d.id} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '8px 12px', background: form.docType === d.id ? '#f0e8e0' : '#f5f0e8', border: `0.5px solid ${form.docType === d.id ? '#8b1a1a' : '#c8beaa'}`, borderRadius: '3px', cursor: 'pointer' }}>
                                        <div style={{ flex: 1 }}>
                                            <div style={{ fontSize: '13px', fontFamily: 'sans-serif', color: '#1e2a3a', fontWeight: form.docType === d.id ? 600 : 400 }}>{d.label}</div>
                                            <div style={{ fontSize: '11px', fontFamily: 'sans-serif', color: '#6a7080', marginTop: '1px' }}>{d.desc}</div>
                                        </div>
                                        <input type="radio" name="docType" checked={form.docType === d.id}
                                            onChange={() => set('docType', d.id)}
                                            style={{ width: '16px', height: '16px', accentColor: '#8b1a1a', flexShrink: 0 }} />
                                    </label>
                                ))}
                            </div>
                        </div>

                        {/* Level */}
                        {form.docType && (
                            <>
                                <div style={{ marginBottom: '1.25rem' }}>
                                    <label style={lStyle}>Level</label>
                                    <div style={{ display: 'flex', gap: '10px' }}>
                                        {[['federal','Federal'],['state','State / Local']].map(([val, label]) => (
                                            <button key={val} onClick={() => set('level', val)}
                                                style={{ flex: 1, padding: '8px', background: form.level === val ? '#8b1a1a' : '#f5f0e8', color: form.level === val ? '#f5f0e8' : '#4a5060', border: `0.5px solid ${form.level === val ? '#8b1a1a' : '#c8beaa'}`, borderRadius: '3px', fontSize: '13px', fontFamily: 'sans-serif', cursor: 'pointer' }}>
                                                {label}
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                {form.level === 'state' && (
                                    <div style={{ marginBottom: '1.25rem' }}>
                                        <label style={lStyle}>State</label>
                                        <select style={iStyle} value={form.state} onChange={e => set('state', e.target.value)}>
                                            <option value="">Select state...</option>
                                            {US_STATES.map(s => <option key={s} value={s}>{s}</option>)}
                                        </select>
                                    </div>
                                )}

                                <div style={{ marginBottom: '1.25rem' }}>
                                    <label style={lStyle}>Title or identifier (optional)</label>
                                    <input style={iStyle} type="text"
                                        placeholder="e.g. Executive Order 14110, The SAVE Act, City Ordinance 2026-14..."
                                        value={form.docTitle} onChange={e => set('docTitle', e.target.value)} />
                                </div>

                                <div style={{ marginBottom: '1.25rem' }}>
                                    <label style={lStyle}>Issuing authority (optional)</label>
                                    <input style={iStyle} type="text"
                                        placeholder="e.g. President Biden, EPA, Virginia General Assembly, Richmond City Council..."
                                        value={form.issuer} onChange={e => set('issuer', e.target.value)} />
                                </div>

                                <div style={{ marginBottom: '1.25rem' }}>
                                    <label style={lStyle}>Document text — paste here *</label>
                                    <textarea style={{ ...iStyle, resize: 'vertical' }} rows={10}
                                        placeholder="Paste the full text or key sections of the law, regulation, executive order, or policy here..."
                                        value={form.docText} onChange={e => set('docText', e.target.value)} />
                                    <div style={{ fontSize: '11px', fontFamily: 'sans-serif', color: form.docText.length > 50 ? '#3b6d11' : '#8a8070', marginTop: '3px' }}>
                                        {form.docText.length === 0
                                            ? 'Paste the document text above to continue'
                                            : form.docText.length < 50
                                                ? `${form.docText.length} characters — paste more text`
                                                : `✓ ${form.docText.length.toLocaleString()} characters`}
                                    </div>
                                </div>

                                <div style={{ marginBottom: '1.5rem' }}>
                                    <label style={lStyle}>Specific concerns or questions (optional)</label>
                                    <textarea style={{ ...iStyle, resize: 'vertical' }} rows={3}
                                        placeholder="Any specific section, provision, or concern you want the analysis to focus on..."
                                        value={form.concerns} onChange={e => set('concerns', e.target.value)} />
                                </div>
                            </>
                        )}

                        <button style={{ ...btnPrimary, opacity: !canProceedStep1 ? 0.5 : 1 }}
                            disabled={!canProceedStep1}
                            onClick={() => setStep(2)}>
                            Next: Select analysis →
                        </button>
                    </div>
                )}

                {/* ── STEP 2: Analysis selection ── */}
                {step === 2 && (
                    <div>
                        <div style={{ fontSize: '18px', fontWeight: 600, color: '#1e2a3a', fontFamily: "'EB Garamond', Georgia, serif", marginBottom: '1.5rem' }}>
                            Step 2 — What do you want to know?
                        </div>

                        {/* Document summary */}
                        <div style={{ background: '#ede8dc', border: '0.5px solid #c8beaa', borderRadius: '3px', padding: '10px 14px', marginBottom: '1.5rem', fontSize: '12px', fontFamily: 'sans-serif', color: '#4a5060' }}>
                            Analyzing: <strong style={{ color: '#1e2a3a' }}>{form.docTitle || selectedDocType?.label}</strong>
                            {form.issuer && <span> · Issued by: <strong style={{ color: '#1e2a3a' }}>{form.issuer}</strong></span>}
                            <span> · {form.docText.length.toLocaleString()} characters</span>
                        </div>

                        {/* Analysis checkboxes */}
                        <div style={{ marginBottom: '1.5rem' }}>
                            <label style={lStyle}>Select analysis sections (all selected by default)</label>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                {ANALYSIS_TYPES.map(a => (
                                    <label key={a.id} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 12px', background: form.analysisTypes.includes(a.id) ? '#f0e8e0' : '#f5f0e8', border: `0.5px solid ${form.analysisTypes.includes(a.id) ? '#8b1a1a' : '#c8beaa'}`, borderRadius: '3px', cursor: 'pointer' }}>
                                        <div style={{ flex: 1 }}>
                                            <div style={{ fontSize: '13px', fontFamily: 'sans-serif', color: '#1e2a3a', fontWeight: 600 }}>{a.label}</div>
                                            <div style={{ fontSize: '11px', fontFamily: 'sans-serif', color: '#6a7080', marginTop: '1px' }}>{a.desc}</div>
                                        </div>
                                        <input type="checkbox"
                                            checked={form.analysisTypes.includes(a.id)}
                                            onChange={() => toggleAnalysis(a.id)}
                                            style={{ width: '16px', height: '16px', accentColor: '#8b1a1a', flexShrink: 0, cursor: 'pointer' }} />
                                    </label>
                                ))}
                            </div>
                        </div>

                        {error && (
                            <div style={{ background: '#fdf0ee', border: '0.5px solid #d4a8a0', borderRadius: '3px', padding: '10px 12px', fontSize: '13px', fontFamily: 'sans-serif', color: '#8b1a1a', marginBottom: '1rem' }}>
                                {error}
                            </div>
                        )}

                        <div style={{ display: 'flex', gap: '10px' }}>
                            <button style={btnSecondary} onClick={() => setStep(1)}>← Back</button>
                            <button style={{ ...btnPrimary, opacity: form.analysisTypes.length === 0 || loading ? 0.6 : 1 }}
                                disabled={form.analysisTypes.length === 0 || loading}
                                onClick={generate}>
                                {loading ? 'Analyzing...' : 'Generate Analysis →'}
                            </button>
                        </div>

                        {loading && (
                            <div style={{ marginTop: '1rem', padding: '1rem', background: '#f5f0e8', border: '0.5px solid #c8beaa', borderRadius: '3px', fontSize: '13px', fontFamily: 'sans-serif', color: '#6a7080', lineHeight: 1.6 }}>
                                ⏳ Analyzing the document for constitutional concerns, authority, and legal precedent...
                                <br />This may take 20-30 seconds for a thorough analysis.
                            </div>
                        )}
                    </div>
                )}

                {/* ── STEP 3: Result ── */}
                {step === 3 && result && (
                    <div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '10px' }}>
                            <div>
                                <div style={{ fontSize: '18px', fontWeight: 600, color: '#1e2a3a', fontFamily: "'EB Garamond', Georgia, serif" }}>
                                    Analysis complete
                                </div>
                                <div style={{ fontSize: '13px', fontFamily: 'sans-serif', color: '#6a7080', marginTop: '4px' }}>
                                    {result.docTitle}
                                </div>
                            </div>
                            <button onClick={downloadWord} style={btnPrimary}>
                                ⬇ Download Report
                            </button>
                        </div>

                        {/* Analysis display */}
                        <div style={{ background: '#fff', border: '0.5px solid #c8beaa', borderRadius: '3px', padding: '2rem', fontFamily: 'Georgia, serif', fontSize: '13px', lineHeight: 1.8, color: '#1a1a1a', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
                            {result.analysis.split('\n').map((line, i) => {
                                if (line.startsWith('## ')) {
                                    return (
                                        <div key={i} style={{ fontSize: '15px', fontWeight: 600, color: '#1e2a3a', fontFamily: 'sans-serif', marginTop: i === 0 ? 0 : '1.5rem', marginBottom: '8px', paddingBottom: '6px', borderBottom: '0.5px solid #c8beaa' }}>
                                            {line.replace('## ', '')}
                                        </div>
                                    );
                                }
                                if (line.startsWith('- ') || line.startsWith('• ')) {
                                    return <div key={i} style={{ paddingLeft: '1rem', marginBottom: '4px' }}>• {line.slice(2)}</div>;
                                }
                                if (!line.trim()) return <div key={i} style={{ height: '8px' }} />;
                                return <div key={i} style={{ marginBottom: '4px' }}>{line}</div>;
                            })}
                        </div>

                        {/* Actions */}
                        <div style={{ marginTop: '1.5rem', display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                            <button onClick={downloadWord} style={btnPrimary}>⬇ Download Report</button>
                            <button onClick={reset} style={btnSecondary}>Analyze another document</button>
                        </div>

                        {/* Disclaimer */}
                        <div style={{ marginTop: '1.5rem', padding: '10px 14px', background: '#f5f0e8', border: '0.5px solid #c8beaa', borderRadius: '3px', fontSize: '11px', fontFamily: 'sans-serif', color: '#8a8070', lineHeight: 1.6 }}>
                            This analysis is for educational and civic engagement purposes. It is not legal advice.
                            Constitutional questions are often fact-specific and courts may reach different conclusions.
                            For matters with significant legal consequences, consult a qualified attorney.
                        </div>
                    </div>
                )}
            </div>

            <Footer />
        </>
    );
};

export default LawCheckerPage;
